import { Field, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { AddressFieldProps } from '../../types/fields';
import { config } from '../../utils/config';
import { Country } from '../../utils/countryData';
import getValidator from '../../utils/getValidator';
import { reportErrorToSentry } from '../../utils/initializeSentry';
import { SelectOption } from '../atoms/Select';
import SelectField from '../formik/SelectField';
import TextField from '../formik/TextField';

const cachedStateOptions: Partial<Record<Country, SelectOption[]>> = {};

const loadStateOptions = async (country: Country) => {
  try {
    const stateOptions: SelectOption[] = await (
      await fetch(`https://checkout-cdn.aboutyou.cloud/data/state-options/${country}.json`)
    ).json();
    cachedStateOptions[country] = stateOptions;
  } catch (err) {
    console.error(err);
    reportErrorToSentry(err);
  }
};

const useStateOptions = (selectedCountry: Country) => {
  const [stateOptions, setStateOptions] = useState<SelectOption[]>([]);
  useEffect(() => {
    const isStateSelectEnabled = config.addressForm[selectedCountry].state?.select?.enabled;
    if (!isStateSelectEnabled) {
      setStateOptions([]);
      return;
    }
    if (selectedCountry in cachedStateOptions) {
      setStateOptions(cachedStateOptions[selectedCountry]!);
      return;
    }
    (async () => {
      setStateOptions([]);
      await loadStateOptions(selectedCountry);
      setStateOptions(cachedStateOptions[selectedCountry] ?? []);
    })();
  }, [selectedCountry]);
  return stateOptions;
};

const StateField = ({ context, testIdPrefix }: AddressFieldProps) => {
  const { formatMessage } = useIntl();
  const { values } = useFormikContext<any>();
  const selectedCountry: Country = values[context].country;
  const stateOptions = useStateOptions(selectedCountry);
  const { state: stateConfig } = config.addressForm[selectedCountry];
  const isSelectMode = !!stateConfig?.select?.enabled;

  if (isSelectMode) {
    return (
      <div className="form__field-container">
        <Field
          name={`${context}.state`}
          component={SelectField}
          label={formatMessage({ id: 'address.state' })}
          options={stateOptions}
          validate={getValidator({ required: true })}
          data-test-id={`${testIdPrefix}-state`}
        />
      </div>
    );
  } else {
    return (
      <div className="form__field-container">
        <Field
          name={`${context}.state`}
          component={TextField}
          label={formatMessage({ id: 'address.state' })}
          maxLength={stateConfig?.maxLength ?? 50}
          validate={getValidator({ minLength: 3, format: 'streetOrCity', ...stateConfig })}
          data-test-id={`${testIdPrefix}-state`}
        />
      </div>
    );
  }
};

export default StateField;
